import { useEffect } from "react";
import { useAuthStatus } from "@/contexts/OptimizedAuthContext";
import { redirectToLogin } from "@/lib/utils/auth";
import { Button } from "@/ui-components/ui/button";
import { Home } from "lucide-react";
import { BackgroundGradient } from "../common/BackgroundGradient";
import { StaticErrorHeader } from "./StaticErrorHeader";

export default function NotFound() {
  const { isSignedIn, isLoaded } = useAuthStatus();

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      redirectToLogin();
    }
  }, [isLoaded, isSignedIn]);

  const handleGoHome = () => {
    // Use window.location for consistency with other error pages
    window.location.href = "/";
  };

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-muted-foreground">Redirecting to login...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <StaticErrorHeader />

      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        <BackgroundGradient />

        {/* Outer blur layer */}
        <div className="absolute inset-0 flex items-center justify-center z-0">
          <div className="w-[600px] h-[600px] bg-white/20 rounded-full blur-3xl"></div>
        </div>

        {/* Middle blur layer */}
        <div className="absolute inset-0 flex items-center justify-center z-0">
          <div className="w-[400px] h-[400px] bg-[#01B9D9]/10 rounded-full blur-2xl"></div>
        </div>

        <div className="text-center px-6 relative z-10">
          {/* Glass container with depth */}
          <div className="relative">
            {/* Shadow layer for depth */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-white/10 rounded-3xl blur-xl transform translate-y-2"></div>

            {/* Main glass card */}
            <div className="relative backdrop-blur-xl bg-white/30 rounded-3xl border border-white/50 shadow-2xl px-6 py-10 sm:px-12 sm:py-16 md:px-20 md:py-20">
              {/* Inner glow layer */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/50 via-transparent to-transparent rounded-3xl"></div>

              <div className="relative">
                {/* 404 Display with subtle background */}
                <div className="mb-8 relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-48 h-48 bg-gradient-to-br from-black/10 to-transparent rounded-full blur-2xl"></div>
                  </div>
                  <h1 className="text-6xl sm:text-8xl md:text-9xl font-bold text-black/15 relative tracking-tight">
                    404
                  </h1>
                </div>

                {/* Message */}
                <h2 className="text-2xl md:text-3xl font-semibold text-black/75 mb-2">
                  This spec didn't make it to the story.
                </h2>

                <p className="text-black/50 mb-10">
                  The page you're looking for doesn't exist.
                </p>

                {/* Single action button with enhanced styling */}
                <Button
                  onClick={handleGoHome}
                  className="bg-[#01B9D9] hover:bg-[#01B9D9]/90 text-white shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <Home className="mr-2 h-4 w-4" />
                  Go Home
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
